import React from "react";
import { Authenticated } from "@refinedev/core";
import { Flex } from "antd";
import { Card } from "@libertify/ui";
import { SubPageHeader } from "components/SubPageHeader/";
import { StudioTopBar } from "components/layout/header/StudioTopBar";
import { useSession } from "hooks/useSession";
import { InsightsPanel } from "../../analytics/components/InsightsPanel/InsightsPanel";
import type { AnalyticsDateRange } from "../../projects/hooks/useAnalyticsDateRange";
import { useProjectThumbnail } from "../../projects/hooks/useProjectThumbnail";
import { AnalyticsHeader } from "./AnalyticsHeader";
import { AnalyticsTabs } from "./AnalyticsTabs";
import { ProjectKpiRow } from "./ProjectKpiRow";

export type AnalyticsPageLayoutProps = {
  authKey: string;
  projectId: string | undefined;
  projectTitle: string | undefined;
  onBack: (() => void) | undefined;
  /** The range the KPI row follows: the one picked on the page. */
  dateRange: AnalyticsDateRange;
  /** The version picked on the page, which the KPI row's video figures follow. */
  version?: string;
  children: React.ReactNode;
};

/**
 * Page shell of every project analytics tab: header bar, banner, KPIs, then
 * the tabs card next to the project insights.
 */
export const AnalyticsPageLayout: React.FC<AnalyticsPageLayoutProps> = ({
  authKey,
  projectId,
  projectTitle,
  onBack,
  dateRange,
  version,
  children,
}) => {
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  const { thumbnail } = useProjectThumbnail(organizationId, projectId);

  return (
    <Authenticated key={authKey}>
      <SubPageHeader
        itemId={projectId}
        itemTitle={projectTitle ?? ""}
        page="analytics"
        source="projects"
        spaced
        onBack={onBack}
        rightContent={<StudioTopBar />}
      />
      <Flex vertical gap={24}>
        <AnalyticsHeader
          projectId={projectId}
          title={projectTitle ?? ""}
          image={thumbnail}
        />
        <ProjectKpiRow
          projectId={projectId}
          dateRange={dateRange}
          version={version}
        />
        <Flex gap={20} align="flex-start">
          <Card padding="none" style={{ flex: "1 1 auto", minWidth: 0 }}>
            <AnalyticsTabs />
            <Flex vertical gap={24} style={{ padding: 24 }}>
              {children}
            </Flex>
          </Card>
          {organizationId && projectId ? (
            <InsightsPanel
              key={projectId}
              organizationId={organizationId}
              workspaceId={undefined}
              projectId={projectId}
            />
          ) : null}
        </Flex>
      </Flex>
    </Authenticated>
  );
};
